import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { OfflinePage } from './OfflinePage';

export interface OfflineGateProps {
  children: ReactNode;
}

/**
 * Full-viewport offline overlay. Listens to `online` / `offline` and
 * dismisses automatically when connectivity returns.
 */
export function OfflineGate({ children }: OfflineGateProps): JSX.Element {
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine,
  );

  useEffect(() => {
    function handleOffline(): void {
      setIsOffline(true);
    }
    function handleOnline(): void {
      setIsOffline(false);
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const handleRetry = useCallback(() => {
    if (navigator.onLine) {
      setIsOffline(false);
      return;
    }
    window.location.reload();
  }, []);

  return (
    <>
      {children}
      {isOffline ? <OfflinePage onRetry={handleRetry} /> : null}
    </>
  );
}
